import _ from 'lodash';
import { translateBlockValue, translateComponent } from 'i18n';
import { formatDisplay } from 'helpers/formatHelper';
import { YEAR_COLUMN_KEY } from 'business/constants';
import {
    ChartSettingsT,
    DataTypes as DT,
    OrderedColumnsT,
} from 'business/definitions';
import { dispatch } from 'store/store';
import { UnformatedChart, UnformatedVizData } from './types';
import { getIsChartConceptionEnabled } from './conception';

type MergeStrategyFunction = (
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    settings: ChartSettingsT,
    vizType?: string,
) => UnformatedChart;

/**
 * Find two columns that can be merged together
 * This function is called on the chart initialisation and the user has no influence on its result
 * @returns the mergeable columns if any
 */
const getInitialColumnsToMerge = (
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    isYearlyComparison?: boolean,
    vizType?: string,
) => {
    let firstMergeColumn: ResponseColumnT | undefined;
    let secondMergeColumn: ResponseColumnT | undefined;

    if (isYearlyComparison) {
        // When it is a yearly comparison we merge on the column containing the date
        firstMergeColumn = columns.find(
            (col) =>
                col.key === YEAR_COLUMN_KEY ||
                col.key.includes(YEAR_COLUMN_KEY),
        );
        // We merge the year column with another category column, not the month
        const categoryColumn = columns.find(
            (col) =>
                [DT.category, DT.text].includes(col.type) &&
                col.key !== 'month',
        );
        if (categoryColumn) {
            secondMergeColumn = categoryColumn;
        } else {
            secondMergeColumn = columns.find(
                (col, index) =>
                    col.type === DT.year &&
                    // we don't want to merge a column with itself, because it is COLCEPTION
                    index !== _.indexOf(columns, firstMergeColumn),
            );
        }
    } else {
        // TODO: Fix this ugly thing
        // We force the merge of chapter.code and chapter.label if they are two dimensions
        const chapterColumns = columns.filter((col) =>
            ['chapter.code', 'chapter.label'].includes(col.key),
        );

        if (chapterColumns.length === 2) {
            firstMergeColumn = chapterColumns[0];
            secondMergeColumn = chapterColumns[1];
        } else {
            const emptyColumns = columns.filter((col) => {
                const values = new Set(data.map((d) => d[col.key]));
                return values.size === 1 && !col.isMetric;
            });

            const categoryColumns = columns.filter(
                (col) =>
                    [DT.category, DT.text].includes(col.type) &&
                    !emptyColumns.includes(col),
            );
            const integerColumns = columns.filter(
                (col) =>
                    [DT.integer, DT.year].includes(col.type) &&
                    !emptyColumns.includes(col),
            );
            // In the case of a map, we don't merge the empty columns to display the locations
            const emptyMetricsColumns = columns.filter((col) => {
                const values = new Set(data.map((d) => d[col.key]));
                return values.size === 1 && col.isMetric && vizType !== 'map';
            });
            // First we merge the empty columns, then the categories, then the integers
            const mergeColumns = [
                ...emptyColumns,
                ...categoryColumns,
                ...integerColumns,
                ...emptyMetricsColumns,
            ];
            mergeColumns.push(
                ...columns.filter(
                    (col) => !col.isMetric && !mergeColumns.includes(col),
                ),
            );
            firstMergeColumn = mergeColumns[0];
            secondMergeColumn = mergeColumns[1];
        }
    }
    return { firstMergeColumn, secondMergeColumn };
};

/**
 * Find two columns that can be merged together on a specific dimension
 * using the chart conception settings.
 * If there not any merged column on the dimension, we return the first two columns to merge
 * If there is already a merged column on the dimension, we return it and the next column to merge
 * Else it returns null
 */
const findColumnToMergeOnDimension = (
    chartDimensionKey: keyof OrderedColumnsT,
    columns: ResponseColumnT[],
    chartConceptionSettings: ChartSettingsT['chartConception'],
) => {
    const columnOnDimension =
        chartConceptionSettings
            ?.filter((column) => column.chartDimensionKey === chartDimensionKey)
            .reverse()
            .map((column) => column.id) ?? [];
    const columnsToMerge = columns.filter((col) =>
        columnOnDimension.includes(col.key),
    );
    const baseColumn = columns.find(
        (column) => column.displayOnAxis === chartDimensionKey,
    );
    if (baseColumn && columnsToMerge.length > 0) {
        return {
            firstMergeColumn: baseColumn,
            secondMergeColumn: columnsToMerge[0],
        };
    }
    if (columnsToMerge.length > 1) {
        return {
            firstMergeColumn: {
                ...columnsToMerge[0],
                displayOnAxis: chartDimensionKey,
            },
            secondMergeColumn: {
                ...columnsToMerge[1],
                displayOnAxis: chartDimensionKey,
            },
        };
    }
    return null;
};

/**
 * Find two columns that can be merged together in the chart conception setting
 * It first checks the x dimension (which should always exists), then the z dimension which is optional
 * @returns the mergeable columns if any
 */
const getConceptionColumnToMerge = (
    columns: ResponseColumnT[],
    chartConceptionSettings: ChartSettingsT['chartConception'],
) => {
    const columnToMergeOnX = findColumnToMergeOnDimension(
        'x',
        columns,
        chartConceptionSettings,
    );
    if (columnToMergeOnX) {
        return columnToMergeOnX;
    }
    const columnToMergeOnZ = findColumnToMergeOnDimension(
        'z',
        columns,
        chartConceptionSettings,
    );
    if (columnToMergeOnZ) {
        return columnToMergeOnZ;
    }
    return {
        firstMergeColumn: null,
        secondMergeColumn: null,
    };
};

/**
 * Merge two columns (their label and their data) into one
 * @returns the new chartData and
 */
const mergeColumns = (
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    firstMergeColumn: ResponseColumnT,
    secondMergeColumn: ResponseColumnT,
) => {
    const firstKey = firstMergeColumn.key;
    const secondKey = secondMergeColumn.key;
    const chartColumns: ResponseColumnT[] = columns.filter(
        (col) => ![firstKey, secondKey].includes(col.key),
    );

    const columnKey = `${firstMergeColumn.key} - ${secondMergeColumn.key}`;

    chartColumns.push({
        key: columnKey,
        type: 'category',
        label: [...firstKey.split(' - '), ...secondKey.split(' - ')]
            .map((key) => translateBlockValue(key))
            .join(' - '),
        mergedColumns: [
            ...(firstMergeColumn.mergedColumns ?? [firstKey]),
            ...(secondMergeColumn.mergedColumns ?? [secondKey]),
        ],
        displayOnAxis: firstMergeColumn.displayOnAxis,
    });
    const firstMergeColType = firstMergeColumn.type;
    const secondMergeColType = secondMergeColumn.type;
    const chartData = data.map((d) => {
        const formattedFirstKey = formatDisplay(d[firstKey], firstMergeColType);
        const formattedSecondKey = formatDisplay(
            d[secondKey],
            secondMergeColType,
        );
        if (_.isEqual(formattedFirstKey, formattedSecondKey)) {
            return { ...d, [columnKey]: formattedFirstKey };
        }
        return {
            ...d,
            [columnKey]: `${formattedFirstKey} - ${formattedSecondKey}`,
        };
    });

    return { chartData, chartColumns };
};

/**
 * Merge columns and data so that we can display a chart with more than 3 dimensions.
 * For example if we have a yearly comparison with budget sections, we want to show
 *  Invest 2017 | Fonct 2017 | Invest 2018 | Fonct 2018, which is a merge of two columns.
 */
export const initialMergeChartDimensionStrategy: MergeStrategyFunction = (
    data,
    columns,
    settings,
    vizType,
) => {
    const { isYearlyComparison } = settings;
    const { firstMergeColumn, secondMergeColumn } = getInitialColumnsToMerge(
        data,
        columns,
        isYearlyComparison,
        vizType,
    );
    if (firstMergeColumn && secondMergeColumn) {
        return mergeColumns(data, columns, firstMergeColumn, secondMergeColumn);
    }
    // If we haven't found two columns we simple return the original data
    return { chartData: data, chartColumns: columns };
};

/**
 * Merge columns and data so that we can display a chart with more than 3 dimensions.
 * This function is used when we have a chart conception setting.
 */
export const conceptionMergeChartDimensionStrategy: MergeStrategyFunction = (
    data,
    columns,
    settings,
) => {
    const { firstMergeColumn, secondMergeColumn } = getConceptionColumnToMerge(
        columns,
        settings.chartConception,
    );

    if (firstMergeColumn && secondMergeColumn) {
        return mergeColumns(data, columns, firstMergeColumn, secondMergeColumn);
    }

    // If we haven't found two columns we simple return the original data
    return { chartData: data, chartColumns: columns };
};

export const mergeChartDimensionsWithContext =
    (mergeChartDimensions: MergeStrategyFunction) =>
    (context: UnformatedVizData): UnformatedVizData => {
        const {
            chartData,
            chartColumns,
            context: { settings, vizType },
        } = context;
        let mergedChartDimensions: UnformatedChart = {
            chartData,
            chartColumns,
        };
        if (settings.isYearlyComparison && chartColumns.length === 5) {
            // Sometimes a graph with 4 dimensions gets pivoted to form a yearly comparison.
            // That adds a dimension. We do a first merge here, we could use a while in the future,
            // but we need to ensure that mergeChartDimensions indeed reduces the number of columns.
            mergedChartDimensions = mergeChartDimensions(
                chartData,
                chartColumns,
                settings,
                vizType,
            );
        }
        // In case all the columns are projected on X and Y axis, we need to merge them while we have more than 2 dimensions
        // Else (and in the initial case) we need to wait for 3 dimensions to be able to merge them

        const isChartConceptionEnabled = getIsChartConceptionEnabled(
            settings,
            chartColumns,
            vizType,
        );
        const dimensionToWaitFor =
            settings.chartConception && isChartConceptionEnabled
                ? new Set(
                      settings.chartConception.map(
                          (col) => col.chartDimensionKey,
                      ),
                  ).size
                : 3;

        let previousColumns = mergedChartDimensions.chartColumns
            .map((col) => col.key)
            .sort();
        while (mergedChartDimensions.chartColumns.length > dimensionToWaitFor) {
            mergedChartDimensions = mergeChartDimensions(
                mergedChartDimensions.chartData,
                mergedChartDimensions.chartColumns,
                settings,
                vizType,
            );
            // Sometimes we can't merge the columns
            // This can be caused by a new data type or a request misconception
            // To avoid an infinite loop, we check that the number of columns has changed
            // If not, we break the loop and log an error
            const newColumnFields = mergedChartDimensions.chartColumns
                .map((col) => col.key)
                .sort();
            if (
                JSON.stringify(newColumnFields) ===
                JSON.stringify(previousColumns)
            ) {
                dispatch.vision.notifyUser({
                    message: translateComponent('errors.invalidRequest'),
                    snackType: 'error',
                });
                throw new Error(
                    `We couldn't merge the following columns: ${mergedChartDimensions.chartColumns.map(
                        (col) => col.key,
                    )}`,
                );
            }
            previousColumns = newColumnFields;
        }
        return {
            ...context,
            chartData: mergedChartDimensions.chartData,
            chartColumns: mergedChartDimensions.chartColumns,
        };
    };
